import { cn } from "@/lib/utils";

interface RiskGaugeProps {
  score: number;
  label: string;
  className?: string;
}

const colorMap: Record<string, { ring: string; text: string; bg: string }> = {
  low: { ring: "text-green-500", text: "text-green-400", bg: "bg-green-500/10" },
  medium: { ring: "text-yellow-500", text: "text-yellow-400", bg: "bg-yellow-500/10" },
  high: { ring: "text-orange-500", text: "text-orange-400", bg: "bg-orange-500/10" },
  critical: { ring: "text-red-500", text: "text-red-400", bg: "bg-red-500/10" },
};

export function RiskGauge({ score, label, className }: RiskGaugeProps) {
  const colors = colorMap[label] ?? colorMap.low;
  const circumference = 2 * Math.PI * 54; // radius=54
  const progress = (score / 100) * circumference * 0.75; // 270-degree arc

  return (
    <div className={cn("flex flex-col items-center", className)}>
      <div className="relative h-48 w-48">
        <svg viewBox="0 0 120 120" className="h-full w-full -rotate-[135deg]">
          {/* Background arc */}
          <circle
            cx="60"
            cy="60"
            r="54"
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${circumference * 0.75} ${circumference * 0.25}`}
            className="text-muted/30"
          />
          {/* Score arc */}
          <circle
            cx="60"
            cy="60"
            r="54"
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${progress} ${circumference - progress}`}
            className={cn(colors.ring, "transition-all duration-1000 ease-out")}
          />
        </svg>
        {/* Center text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={cn("text-4xl font-bold tabular-nums", colors.text)}>
            {score}
          </span>
          <span className="text-xs text-muted-foreground">/ 100</span>
        </div>
      </div>
      <div className={cn("mt-2 rounded-full px-3 py-1 text-xs font-semibold uppercase", colors.bg, colors.text)}>
        {label} Risk
      </div>
    </div>
  );
}
